import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["strategia_fiscale", "policy_interne", "evidenze_controlli", "ade", "certificazioni", "altro"];
const CATEGORY_LABEL = {
  strategia_fiscale: "Strategia Fiscale", policy_interne: "Policy Interne",
  evidenze_controlli: "Evidenze Controlli", ade: "Comunicazioni ADE",
  certificazioni: "Certificazioni", altro: "Altro",
};

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "altro", tags: [], file_name: "", document_date: "" });

  const load = async () => { const { data } = await api.get("/documents"); setDocs(data); };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return toast.error("Titolo richiesto");
    try {
      await api.post("/documents", form);
      toast.success("Documento aggiunto");
      setOpen(false); setForm({ title: "", description: "", category: "altro", tags: [], file_name: "", document_date: "" });
      load();
    } catch (e) { toast.error("Errore"); }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`); load();
  };

  const counts = {};
  CATEGORIES.forEach((c) => { counts[c] = docs.filter((d) => d.category === c).length; });

  const filtered = filter ? docs.filter((d) => d.category === filter) : docs;

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Repository</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1">Documenti</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="add-doc-btn"><Upload size={14} className="mr-1" /> Aggiungi documento</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuovo documento</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Titolo" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="rounded-sm" data-testid="doc-title" />
              <Input placeholder="Nome file (es. policy_2025.pdf)" value={form.file_name} onChange={(e) => setForm((f) => ({ ...f, file_name: e.target.value }))} className="rounded-sm" />
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}</SelectContent>
              </Select>
              <Textarea placeholder="Descrizione" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="rounded-sm" />
              <Input type="date" value={form.document_date} onChange={(e) => setForm((f) => ({ ...f, document_date: e.target.value }))} className="rounded-sm" />
            </div>
            <DialogFooter><Button onClick={create} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-doc">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-3 card-flat p-4">
          <div className="label-tag mb-3">Categorie</div>
          <button onClick={() => setFilter(null)} className={`w-full text-left text-sm py-2 px-2 rounded-sm ${!filter ? "bg-slate-100 font-medium" : "hover:bg-slate-50"}`}>Tutte</button>
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setFilter(c)} className={`w-full text-left text-sm py-2 px-2 rounded-sm flex justify-between ${filter === c ? "bg-slate-100 font-medium" : "hover:bg-slate-50"}`}>
              <span>{CATEGORY_LABEL[c]}</span><span className="text-xs text-slate-500 data-num">{counts[c]}</span>
            </button>
          ))}
        </Card>

        <div className="col-span-9">
          <div className="border-2 border-dashed border-slate-300 bg-slate-50 rounded-sm p-8 text-center text-slate-500 text-sm" data-testid="dropzone">
            <Upload size={20} className="mx-auto mb-2" /> Trascina qui i file oppure usa "Aggiungi documento".
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            {filtered.length === 0 && <div className="col-span-3 text-slate-500 text-sm text-center py-10">Nessun documento</div>}
            {filtered.map((d) => (
              <Card key={d.id} className="card-flat p-5 hover:border-slate-400 transition-colors" data-testid={`doc-${d.id}`}>
                <div className="flex items-start justify-between">
                  <FileText size={28} className="text-slate-400" strokeWidth={1.5} />
                  <button onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                </div>
                <div className="font-medium text-slate-900 mt-3 truncate">{d.title}</div>
                <div className="text-xs text-slate-500 truncate">{d.file_name}</div>
                <div className="mt-3 flex items-center justify-between">
                  <Badge variant="outline" className="rounded-sm text-[10px]">{CATEGORY_LABEL[d.category]}</Badge>
                  <span className="text-xs text-slate-500 data-num">{d.document_date}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
